/**
 * Commission policies and their versions.
 *
 * A version is a draft until published, and fixed for good after: a change is a
 * new version, and the commission already computed keeps the version it was
 * computed under. A rule needs a window in months unless it is explicitly
 * unbounded — open-ended earning is a decision written down, never a default
 * (owner, 2026-10-19). There is no built-in rate: every figure here is the
 * organization's own.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Textarea,
  formatDate,
  formatMoney,
  toast,
} from '@connect/web-shared';

import { SERVICE_LABELS, partnerKeys, partnersApi, percent, windowText, type Policy, type Service } from './api';
import { Failed, errorText } from './shared';

interface RuleDraft {
  service: Service;
  rate: string;
  max_discount_rate: string;
  window_months: string;
  unbounded: boolean;
  cap_per_customer: string;
}

const EMPTY_RULE: RuleDraft = {
  service: 'sms',
  rate: '',
  max_discount_rate: '0',
  window_months: '',
  unbounded: false,
  cap_per_customer: '',
};

export function PartnerPoliciesPage() {
  const queryClient = useQueryClient();
  const policies = useQuery({ queryKey: partnerKeys.policies, queryFn: partnersApi.policies });
  const [naming, setNaming] = useState(false);
  const [drafting, setDrafting] = useState<{ policy: Policy; version?: string } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: partnerKeys.policies });
  const publish = useMutation({
    mutationFn: (version: string) => partnersApi.publish(version),
    onSuccess: async () => {
      toast.success('Published', 'Agreements can now use this version.');
      await refresh();
    },
    onError: (e) => toast.error('Could not publish', errorText(e)),
  });
  const retire = useMutation({
    mutationFn: (version: string) => partnersApi.retire(version),
    onSuccess: async () => refresh(),
    onError: (e) => toast.error('Could not retire', errorText(e)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Commission policies"
        description="What each service earns partners, for how long, and how much of it may be given as a discount."
        actions={
          <Can permission="partners.policy.create">
            <Button onClick={() => setNaming(true)}>
              <Plus className="size-4" />
              New policy
            </Button>
          </Can>
        }
      />
      {policies.isPending ? (
        <LoadingState />
      ) : policies.isError ? (
        <Failed error={policies.error} onRetry={() => void policies.refetch()} />
      ) : policies.data.length === 0 ? (
        <EmptyState title="No policies yet" description="Make a policy, give it a version with its rates, and publish it." />
      ) : (
        policies.data.map((policy) => (
          <Card key={policy.public_id}>
            <CardHeader
              title={policy.name}
              description={policy.description || undefined}
              actions={
                <Can permission="partners.policy.create">
                  <Button size="sm" variant="secondary" onClick={() => setDrafting({ policy })}>
                    New version
                  </Button>
                </Can>
              }
            />
            <CardBody className="space-y-4">
              {policy.versions.map((v) => (
                <div key={v.public_id} className="rounded-md border border-line p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Version {v.version}
                      <Badge tone={v.status === 'published' ? 'success' : v.status === 'draft' ? 'info' : 'neutral'}>
                        {v.status}
                      </Badge>
                      {v.published_at ? <span className="text-xs text-muted">since {formatDate(v.published_at)}</span> : null}
                    </div>
                    <div className="flex gap-2">
                      {v.status === 'draft' ? (
                        <>
                          <Can permission="partners.policy.edit">
                            <Button size="sm" variant="secondary" onClick={() => setDrafting({ policy, version: v.public_id })}>
                              Edit draft
                            </Button>
                          </Can>
                          <Can permission="partners.policy.publish">
                            <Button size="sm" loading={publish.isPending} onClick={() => publish.mutate(v.public_id)}>
                              Publish
                            </Button>
                          </Can>
                        </>
                      ) : v.status === 'published' ? (
                        <Can permission="partners.policy.publish">
                          <Button size="sm" variant="secondary" onClick={() => retire.mutate(v.public_id)}>
                            Retire
                          </Button>
                        </Can>
                      ) : null}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted">
                        <th className="py-1">Service</th>
                        <th className="py-1">Rate</th>
                        <th className="py-1">Max discount</th>
                        <th className="py-1">Window</th>
                        <th className="py-1">Cap per customer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.rules.map((r) => (
                        <tr key={r.service} className="border-t border-line">
                          <td className="py-1">{SERVICE_LABELS[r.service]}</td>
                          <td className="py-1">{percent(r.rate)}</td>
                          <td className="py-1">{percent(r.max_discount_rate)}</td>
                          <td className="py-1">{windowText(r)}</td>
                          <td className="py-1">{r.cap_per_customer ? formatMoney(r.cap_per_customer) : 'None'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {v.note ? <p className="mt-2 text-xs text-muted">{v.note}</p> : null}
                </div>
              ))}
              {policy.versions.length === 0 ? <p className="text-sm text-muted">No versions yet.</p> : null}
            </CardBody>
          </Card>
        ))
      )}
      <NewPolicy open={naming} onClose={() => setNaming(false)} />
      {drafting ? <VersionEditor target={drafting} onClose={() => setDrafting(null)} /> : null}
    </div>
  );
}

function NewPolicy({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () => partnersApi.createPolicy({ name, description }),
    onSuccess: async () => {
      onClose();
      setName('');
      setDescription('');
      await queryClient.invalidateQueries({ queryKey: partnerKeys.policies });
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New policy"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name} loading={create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.isError ? <Alert tone="danger">{errorText(create.error)}</Alert> : null}
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard reseller" />
        <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </div>
    </Modal>
  );
}

function VersionEditor({ target, onClose }: { target: { policy: Policy; version?: string }; onClose: () => void }) {
  const queryClient = useQueryClient();
  const existing = target.version ? target.policy.versions.find((v) => v.public_id === target.version) : undefined;
  const [note, setNote] = useState(existing?.note ?? '');
  const [rules, setRules] = useState<RuleDraft[]>(
    existing?.rules.map((r) => ({
      service: r.service,
      rate: r.rate,
      max_discount_rate: r.max_discount_rate,
      window_months: r.window_months?.toString() ?? '',
      unbounded: r.unbounded,
      cap_per_customer: r.cap_per_customer ?? '',
    })) ?? [{ ...EMPTY_RULE }],
  );
  const body = () => ({
    note,
    rules: rules.map((r) => ({
      service: r.service,
      rate: r.rate,
      max_discount_rate: r.max_discount_rate || '0',
      window_months: r.unbounded || !r.window_months ? null : Number.parseInt(r.window_months, 10),
      unbounded: r.unbounded,
      cap_per_customer: r.cap_per_customer || null,
    })),
  });
  const save = useMutation({
    mutationFn: () =>
      target.version
        ? partnersApi.replaceVersion(target.version, body())
        : partnersApi.createVersion(target.policy.public_id, body()),
    onSuccess: async () => {
      toast.success('Saved as a draft', 'Publish it to use it in agreements.');
      onClose();
      await queryClient.invalidateQueries({ queryKey: partnerKeys.policies });
    },
  });
  const update = (i: number, patch: Partial<RuleDraft>) =>
    setRules((current) => current.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  const used = new Set(rules.map((r) => r.service));
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={target.version ? `Edit ${target.policy.name} draft` : `New version of ${target.policy.name}`}
      description="Rates are percentages of the customer's eligible spending. The discount comes out of the partner's rate."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={rules.some((r) => !r.rate || (!r.unbounded && !r.window_months))} onClick={() => save.mutate()}>
            Save draft
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {save.isError ? <Alert tone="danger">{errorText(save.error)}</Alert> : null}
        {rules.map((rule, i) => (
          <div key={rule.service} className="grid items-end gap-2 rounded-md border border-line p-3 sm:grid-cols-7">
            <Select
              label="Service"
              value={rule.service}
              onChange={(e) => update(i, { service: e.target.value as Service })}
              options={(Object.keys(SERVICE_LABELS) as Service[])
                .filter((s) => s === rule.service || !used.has(s))
                .map((s) => ({ value: s, label: SERVICE_LABELS[s] }))}
            />
            <Input label="Rate %" inputMode="decimal" value={rule.rate} onChange={(e) => update(i, { rate: e.target.value })} />
            <Input
              label="Max discount %"
              inputMode="decimal"
              value={rule.max_discount_rate}
              onChange={(e) => update(i, { max_discount_rate: e.target.value })}
            />
            <Input
              label="Window (months)"
              inputMode="numeric"
              disabled={rule.unbounded}
              value={rule.window_months}
              onChange={(e) => update(i, { window_months: e.target.value.replace(/\D/g, '') })}
            />
            <Checkbox
              label="No end (unbounded)"
              checked={rule.unbounded}
              onChange={(e) => update(i, { unbounded: e.target.checked, window_months: '' })}
            />
            <Input
              label="Cap per customer ₹"
              inputMode="decimal"
              value={rule.cap_per_customer}
              onChange={(e) => update(i, { cap_per_customer: e.target.value })}
            />
            <Button
              variant="ghost"
              aria-label="Remove this rule"
              disabled={rules.length === 1}
              onClick={() => setRules((current) => current.filter((_, j) => j !== i))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        {used.size < 4 ? (
          <Button
            variant="secondary"
            onClick={() =>
              setRules((current) => [
                ...current,
                { ...EMPTY_RULE, service: (Object.keys(SERVICE_LABELS) as Service[]).find((s) => !used.has(s))! },
              ])
            }
          >
            Add a service
          </Button>
        ) : null}
        {rules.some((r) => r.unbounded) ? (
          <Alert tone="warning">
            An unbounded rule pays commission on a customer's spending for as long as the agreement lasts. Use it only
            where the agreement says so explicitly.
          </Alert>
        ) : null}
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </div>
    </Modal>
  );
}
